import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { ORGANIZATION_USER_DETAILS_REFERENCE_DATA_TYPES, OrganizationUserDetailsReferenceData } from "@constants/reference-data";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import {
    getOrganizationUserDetailsReduxRequest,
    updateOrganizationUserDetailsReduxRequest
} from "@redux-requests/organization-user-details";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { OrganizationUserDetailsComponent } from "./organization-user-details-tab-container/organization-user-details-component";
import { getSessionState } from "@redux/selectors/root-selectors";
import { getOrganizationUserDetailsDataState } from "@redux/selectors/organization-user-details-selectors";

export const OrganizationUserDetailsContainer = () => {
    const { axnAdminUserId } = useParams<{ axnAdminUserId: string }>();

    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const {
        isLoadingAxnAdminUser,
        axnAdminUser
    } = useSelector(getOrganizationUserDetailsDataState);
    const referenceData = useReferenceDataSelector<OrganizationUserDetailsReferenceData>(ORGANIZATION_USER_DETAILS_REFERENCE_DATA_TYPES);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadAxnAdminUser, setShouldLoadAxnAdminUser] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.organizationUserDetails && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                ORGANIZATION_USER_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.ORGANIZATION_USER_DETAILS
            );
        }
    }, [referenceData.isLoading.organizationUserDetails, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnAdminUserPropertyBundles &&
            referenceData.axnAdminAvailableUserOrganizationProperties &&
            referenceData.axnAdminAvailableUserRPProperties &&
            referenceData.axnIdentityProviders &&
            referenceData.axnRelyingParties &&
            referenceData.axnAdminUserPropertyCategories &&
            referenceData.documentationLinks
        );
    }, [
        referenceData.axnAdminUserPropertyBundles,
        referenceData.axnAdminAvailableUserOrganizationProperties,
        referenceData.axnAdminAvailableUserRPProperties,
        referenceData.axnIdentityProviders,
        referenceData.axnRelyingParties,
        referenceData.axnAdminUserPropertyCategories,
        referenceData.documentationLinks
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingAxnAdminUser && shouldLoadAxnAdminUser) {
            setShouldLoadAxnAdminUser(false);
            getOrganizationUserDetailsReduxRequest(dispatch)(axnAdminUserId);
        }
    }, [isReferenceDataPresent, isLoadingAxnAdminUser, shouldLoadAxnAdminUser]);

    const isLoading = useMemo(() => {
        return isLoadingAxnAdminUser || referenceData.isLoading.organizationUserDetails;
    }, [isLoadingAxnAdminUser, referenceData.isLoading.organizationUserDetails]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            axnAdminUser &&
            isReferenceDataPresent &&
            !shouldLoadReferenceData &&
            !shouldLoadAxnAdminUser
        );
    }, [isLoading, axnAdminUser, isReferenceDataPresent, shouldLoadReferenceData, shouldLoadAxnAdminUser]);


    const handleSave = async (axnAdminUser: AxnAdminUser) => {
        setRequestInProgress(true);
        await updateOrganizationUserDetailsReduxRequest(dispatch)(axnAdminUser);
        setRequestInProgress(false);
    };

    const resource = resources.pages.organizationUserDetails;

    return (
        <CardContainer>
            <ContentPageHeader
                title={resource.title}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"organization-user-details-page-header"}
                    />
                }
            />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <OrganizationUserDetailsComponent
                        session={session}
                        axnAdminUser={axnAdminUser}
                        axnAdminUserPropertyBundles={referenceData.axnAdminUserPropertyBundles}
                        axnAdminAvailableUserOrganizationProperties={referenceData.axnAdminAvailableUserOrganizationProperties}
                        axnAdminAvailableUserRPProperties={referenceData.axnAdminAvailableUserRPProperties}
                        axnIdentityProviders={referenceData.axnIdentityProviders}
                        auth0IdentityProviders={referenceData.auth0IdentityProviders}
                        axnRelyingParties={referenceData.axnRelyingParties}
                        axnAdminUserPropertyCategories={referenceData.axnAdminUserPropertyCategories}
                        requestInProgress={requestInProgress}
                        setRequestInProgress={setRequestInProgress}
                        handleSave={handleSave}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};